import { parts } from './part'
import { mechanisms } from './mechanism'
import { AXIS_KEYS, type AxisKey, type Rig, type RigNode } from './rig'
import {
  TOE_SPRING,
  ownMass,
  segmentLength,
  type HumanoidModel,
  type SegmentKey,
  type Skeleton,
  type Vec3,
} from '@/domain/hs1'

/**
 * MJCF — der Achsbaum für MuJoCo.
 *
 * WARUM NEBEN DEM URDF-EXPORT UND NICHT STATT SEINER. URDF ist der kleinste
 * gemeinsame Nenner und wird von allen gelesen; es kann dafür drei Dinge nicht,
 * die dieses Modell ausmachen, und der URDF-Export sagt das auch:
 *
 *   – geschlossene Ketten (Kurbelschwinge am Knie, Parallelkinematik am Rumpf),
 *   – Getriebe und Momentgrenzen als Antriebseigenschaft,
 *   – die Kennlinie der Zehenfeder.
 *
 * MJCF kann alle drei. Deshalb ist es hier kein zweiter Export desselben, es ist
 * der Export, in dem das Modell VOLLSTÄNDIG steht — und damit die Grundlage für
 * die Gegenprobe: MuJoCo rechnet vorwärts, dieser Kern rechnet rückwärts, und
 * wo beide auseinandergehen, steht eine Frage.
 *
 * ES WIRD ERZEUGT UND NICHT GEPFLEGT. Dieselbe Regel wie beim URDF: wer einen
 * Punkt verschiebt, bekommt ein anderes MJCF. Eine gepflegte Datei daneben wäre
 * die zweite Wahrheit, die dieses Projekt an jeder Stelle vermeidet.
 *
 * DIE MASSEN KOMMEN AUS DEM RECHENKERN UND NICHT AUS DER STÜCKLISTE. Das ist
 * die wichtigste Entscheidung in dieser Datei. Die Stückliste führt Bauteile
 * mit Massen, sobald das PLM sie liefert — heute liefert es nichts, und ihre
 * Summe wäre null. Der Kern dagegen führt sieben Massensegmente, die zusammen
 * die Eigenmasse ergeben, und GENAU DIESE Verteilung geht in die inverse
 * Dynamik. Nähme das MJCF eine andere, verglichen wir hinterher zwei Roboter.
 *
 * WAS HIER BEWUSST NICHT DRINSTEHT:
 *
 *   – Ein Gleichgewichtsregler. Das Modell beschreibt die Maschine, nicht ihre
 *     Regelung. Ohne Regler fällt es um, und das ist eine Aussage über den
 *     fehlenden Regler und keine über den Roboter.
 *   – Gemessene Kontaktgrössen. Reibung, Nachgiebigkeit und Dämpfung der Sohle
 *     sind ANGENOMMEN; sie stehen mit Wert und Vorbehalt in der Datei.
 *   – Die geschlossenen Ketten als Schleife. MuJoCo könnte sie über
 *     `equality`; die Geometrie der Koppelglieder führt dieses Modell aber
 *     nicht — es führt die Übersetzung. Sie steht deshalb als Kommentar am
 *     Antrieb, mit dem Mechanismus daneben. Das ist ehrlich und nachrechenbar;
 *     erfundene Koppelglieder wären es nicht.
 *
 * FÜNF FALLEN. Die ersten beiden hat der Python-Erzeuger teuer gelernt; die
 * übrigen drei hat der ECHTE MUJOCO-COMPILER gefunden, und keine davon wäre
 * durch Lesen der Datei aufgefallen. Sie stehen in `mjcf.mujoco.test.ts`.
 *
 *   1. DIE MASSE. Wer sie aus einer zweiten Liste nimmt, bekommt eine andere
 *      Gesamtmasse als der Rechenkern — und die Bodenkraft ist dann in beiden
 *      Wegen verschieden, ohne dass jemand den Grund sieht. Hier gibt es keine
 *      zweite Liste.
 *   2. WINKEL IN DER STARTLAGE STEHEN IM BOGENMASS. `compiler angle="degree"`
 *      gilt NICHT für `keyframe qpos`. Daran ist die erste Fassung des
 *      Python-Erzeugers gescheitert, mit einem Schwerpunkt 17 cm zu hoch.
 *      Siehe `keyframe()`.
 *   3. DER ACHSBAUM HAT MEHR KNOTEN, ALS DAS MJCF KÖRPER HABEN DARF. Ein
 *      Körper ohne Masse mit einem Gelenk daran lässt sich nicht integrieren.
 *      Siehe `body()`.
 *   4. DER URSPRUNG DES PUNKTSATZES TAUGT NICHT ALS WURZEL. „more than 6 dofs
 *      in body 'cross'" — siehe `MJCF_ROOT`.
 *   5. DER STEINER-ANTEIL EINER TEILUNG IST RICHTUNGSABHÄNGIG. „inertia must
 *      satisfy A + B >= C" — siehe `splitSeat()`.
 */

/* -------------------------------------------------------------------------
   Schnittstelle
   ------------------------------------------------------------------------- */

export interface MjcfOptions {
  /** Name des Modells in der Datei. */
  name?: string
  /** Stillgelegte Achsen — `knoten.achse` oder `knoten`. */
  off?: ReadonlySet<string>
  /**
   * Rechenschrittweite, s.
   *
   * 1 ms trägt den Gang. Die Zehenfeder mit 195 Nm/rad ist die steifste Feder
   * im Modell; wenn sie aufschwingt, ist 0,5 ms der nächste Schritt. Steht
   * deshalb als Eingabe und nicht als Zahl in der Datei.
   */
  timestep?: number
  /** Die Startlage in GRAD, je `knoten.achse`. Wird im Keyframe umgerechnet. */
  pose?: Readonly<Record<string, number>>
  /**
   * Die Modellkennung für den Dateikopf.
   *
   * Sie steht als Kommentar in der Datei und beantwortet die Frage, die nach
   * einem halben Jahr gestellt wird: WELCHER Punktsatz steckt hier drin. Ohne
   * sie sehen zwei Dateien aus zwei Ständen gleich aus. Siehe
   * `handoff/fingerprint.ts`.
   */
  modelId?: string
}

/** Ein Gelenk, wie es in der Datei steht — in genau dieser Reihenfolge. */
export interface MjcfJoint {
  /** Name in der Datei, mit Seitenkennung. */
  name: string
  node: string
  axis: AxisKey
  side: '' | 'R' | 'L'
  /** Der Körper, in dem es steht. */
  body: string
  lo: number
  hi: number
  /** Momentgrenze aus der Auslegung, Nm. */
  moment: number
  /** Passiv: die Zehenfeder bekommt keinen Antrieb. */
  spring: boolean
}

/**
 * Eine Achse, die das MJCF NICHT tragen kann — mit dem Grund.
 *
 * KEINE FUSSNOTE, SONDERN DAS ERGEBNIS. MuJoCo integriert keinen Körper ohne
 * Masse; eine Achse, deren Glied im Massenmodell nicht vorkommt, lässt sich
 * nicht rechnen. Das ist keine Schwäche des Exports, sondern eine Lücke im
 * Modell, und sie gehört sichtbar nach oben statt still in die Datei.
 */
export interface MjcfGap {
  node: string
  axes: AxisKey[]
  reason: string
}

export interface MjcfResult {
  xml: string
  /** Die Gelenke in Dateireihenfolge — dieselbe, die `qpos` erwartet. */
  joints: MjcfJoint[]
  /** Die Körper in Dateireihenfolge. */
  bodies: string[]
  /** Summe der Körpermassen, kg. Muss die Eigenmasse treffen. */
  mass: number
  gaps: MjcfGap[]
}

/**
 * Reibbeiwert zwischen Sohle und Boden.
 *
 * ANGENOMMEN UND NICHT GEMESSEN. 0,8 ist der übliche Wert für Gummi auf
 * trockenem Beton. Er steht hier als benannte Konstante, damit man ihn findet,
 * wenn eine Messung ihn ersetzt — und damit niemand ihn für einen Messwert
 * hält.
 */
export const GROUND_FRICTION = 0.8

/** Dämpfung je Drehgelenk, Nms/rad. Klein, nur gegen numerisches Zittern. */
const JOINT_DAMPING = 0.2

/** Radius der Kontaktkugeln an der Sohle, m. */
const CONTACT_RADIUS = 0.012

/** Millimeter im Achsbaum, Meter in MJCF. */
const M = (mm: number): number => mm / 1000
const DEG = Math.PI / 180

/* -------------------------------------------------------------------------
   Die Massensitze — woher die Körper ihre Masse bekommen
   ------------------------------------------------------------------------- */

/**
 * Welches Massensegment auf welchem Knoten sitzt und wohin es reicht.
 *
 * `toward` ist der Knoten, zu dem sich das Segment erstreckt — daraus kommen
 * Richtung und Lage des Schwerpunkts. `null` heisst Punktmasse: Rumpf und Kopf
 * führt der Kern ohne Achse, und eine erfundene Achse gäbe ihnen eine
 * Vorzugsrichtung, die im Rechenkern nicht steht.
 *
 * DER OBERSCHENKEL HÄNGT AN `hipN` UND NICHT AN `hipG`: die Hüfte ist im
 * Achsbaum drei Knoten, und das Bein sitzt unter der letzten Achse. Wer es an
 * die erste hängt, bekommt einen Oberschenkel, der beim Abspreizen stehen
 * bleibt.
 */
interface Seat {
  segment: Exclude<SegmentKey, 'payload'>
  node: string
  toward: string | null
}

const SEATS: readonly Seat[] = [
  { segment: 'trunk', node: 'pelvis', toward: null },
  { segment: 'head', node: 'headC', toward: null },
  { segment: 'thigh', node: 'hipN', toward: 'knee' },
  { segment: 'shank', node: 'knee', toward: 'ankle' },
  { segment: 'foot', node: 'ankle', toward: 'mtp' },
  { segment: 'upperArm', node: 'shoulder', toward: 'elbow' },
  { segment: 'forearm', node: 'elbow', toward: 'wrist' },
]

/** Was ein Körper an Masse trägt, im eigenen Rahmen. */
interface Inertia {
  label: string
  mass: number
  /** Schwerpunkt im Körperrahmen, m. */
  com: Vec3
  /** Trägheit quer zur Längsachse, kgm². */
  transverse: number
  /** Trägheit um die Längsachse, kgm². */
  axial: number
  /** Längsachse als Einheitsvektor — `null` bei einer Punktmasse. */
  axis: Vec3 | null
}

/**
 * Die Massen der Körper aus den Segmenten des Rechenkerns.
 *
 * DER SCHWERPUNKT STEHT, WO DER KERN IHN HINSETZT: `com` ist der Anteil der
 * Segmentlänge vom proximalen Gelenk aus, und die Richtung nimmt dieser Export
 * aus dem Achsbaum — vom tragenden Knoten zum nächsten. Beide Zahlen sind
 * dieselben, mit denen die inverse Dynamik rechnet.
 *
 * DIE TRÄGHEIT QUER ZUR ACHSE kommt aus dem Trägheitsradius des Kerns
 * (`gyration`, Anteil der Segmentlänge). DIE TRÄGHEIT UM DIE ACHSE führt der
 * Kern nicht — sie geht in eine ebene Momentenrechnung nicht ein. MJCF verlangt
 * sie. Sie wird deshalb aus dem Bauteilradius als Vollzylinder gerechnet und
 * ist die einzige Grösse in dieser Datei, die nicht im Kern steht. Wo es kein
 * Bauteil gibt, bleibt sie gleich der Querträgheit — eine Kugel, und damit die
 * ungünstige, nicht die bequeme Annahme.
 */
function seats(model: HumanoidModel, skeleton: Skeleton, rig: Rig): Map<string, Inertia> {
  const radius = new Map<string, number>()
  for (const p of parts(model, skeleton)) {
    if (!radius.has(p.carrier)) radius.set(p.carrier, p.radius)
  }

  const out = new Map<string, Inertia>()
  for (const seat of SEATS) {
    const item = model.masses[seat.segment]
    if (!item || item.kg <= 0) continue
    const laenge = segmentLength(model, seat.segment)
    const richtung = seat.toward ? towardVector(rig, seat.node, seat.toward) : null
    const einheit = richtung ? unit(richtung) : null

    /* Der Schwerpunkt liegt auf dem Anteil `com` der Strecke zum nächsten
       Knoten. Bei einer Punktmasse steht er im Ursprung des Knotens — ausser
       beim Rumpf, dessen Mitte der Kern eigens bestimmt. */
    const com: Vec3 = richtung
      ? { x: richtung.x * item.com, y: richtung.y * item.com, z: richtung.z * item.com }
      : seat.segment === 'trunk'
        ? trunkCentreOf(rig)
        : { x: 0, y: 0, z: 0 }

    const k = item.gyration * laenge
    const transverse = item.kg * k * k
    const r = radius.get(seat.node)
    /* EINE PUNKTMASSE BLEIBT ISOTROP. Rumpf und Kopf führt der Kern ohne
       Achse; ihnen über einen Bauteilradius eine Längsträgheit zu geben, hiesse
       eine Vorzugsrichtung zu erfinden, die im Kern nicht steht — und die
       Gierträgheit des Rumpfs um den Faktor neun zu ändern. */
    const axial = richtung && r !== undefined ? (item.kg * r * r) / 2 : transverse

    out.set(seat.node, {
      label: item.label,
      mass: item.kg,
      com,
      transverse,
      axial,
      axis: einheit,
    })
  }

  splitTrunk(out, rig)
  splitToe(out, model, skeleton, rig)
  return out
}

/* -------------------------------------------------------------------------
   Ein Segment auf zwei Körper — die eine Regel für beide Fälle
   ------------------------------------------------------------------------- */

/**
 * Teilt ein Massensegment auf zwei Körper, ohne die Bilanz zu ändern.
 *
 * WARUM ES ÜBERHAUPT NÖTIG IST. Der Rechenkern führt sieben Massensegmente. Er
 * darf das: er rechnet Momente um Gelenke, und dafür genügt eine Masse mit
 * Schwerpunkt und Trägheitsradius. MuJoCo integriert Körper, und jeder Körper,
 * der sich gegen einen anderen dreht, braucht eigene Masse — sonst ist die
 * Massenmatrix singulär und der Löser bricht ab. Zwei Stellen im Modell
 * treffen das: der Rumpf, den das Kreuzgelenk in zwei Hälften teilt, und der
 * Fuss, den das Zehengrundgelenk teilt.
 *
 * WAS DIE TEILUNG ERHÄLT — und das ist der Punkt, an dem sie keine Erfindung
 * ist. Drei Grössen bleiben Zahl für Zahl, was sie im Kern waren:
 *
 *   – die MASSE, weil die Anteile sich zu eins ergänzen,
 *   – der SCHWERPUNKT, weil der zweite Teil dorthin rückt, wo er den ersten
 *     ausgleicht,
 *   – die TRÄGHEIT um den gemeinsamen Schwerpunkt, weil der Steiner-Anteil,
 *     den das Auseinanderrücken erzeugt, von den Eigenträgheiten abgezogen
 *     wird.
 *
 * DER STEINER-ANTEIL IST RICHTUNGSABHÄNGIG, und ihn als eine Zahl von allen
 * drei Richtungen abzuziehen war der zweite Fehler, den der echte Compiler
 * gefunden hat: „inertia must satisfy A + B >= C". Beide Teile rücken ENTLANG
 * der Segmentachse auseinander. Eine Verschiebung entlang einer Achse ändert
 * die Trägheit UM diese Achse nicht — sie ändert nur die beiden quer dazu.
 * Abgezogen wird deshalb quer, und längs bleibt stehen.
 *
 * ANGENOMMEN ist allein die VERTEILUNG innerhalb des Segments: gleichmässig
 * entlang der Länge. Wer eine bessere Verteilung hat, ändert `anteil` — an der
 * Bilanz ändert sich dadurch nichts.
 *
 * `anteil` ist der Massenanteil des ABGETRENNTEN Teils, `comB` sein
 * Schwerpunkt im Rahmen des ursprünglichen Sitzes, `achse` die Richtung, in
 * die geteilt wird.
 */
function splitSeat(
  sitz: Inertia,
  anteil: number,
  comB: Vec3,
  labelB: string,
  achse: Vec3 | null,
): { a: Inertia; b: Inertia } | null {
  if (!(anteil > 0) || anteil >= 1) return null
  const mB = sitz.mass * anteil
  const mA = sitz.mass - mB
  if (mA <= 0) return null

  /* Der Rest rückt so weit, dass der gemeinsame Schwerpunkt bleibt. */
  const comA: Vec3 = {
    x: (sitz.mass * sitz.com.x - mB * comB.x) / mA,
    y: (sitz.mass * sitz.com.y - mB * comB.y) / mA,
    z: (sitz.mass * sitz.com.z - mB * comB.z) / mA,
  }

  /* Was das Auseinanderrücken an Trägheit erzeugt, muss von den
     Eigenträgheiten wieder herunter — sonst wäre der geteilte Rumpf träger als
     der ganze, und das MJCF schwerer zu drehen als die Rechnung daneben.
     NUR QUER: siehe Kopfkommentar. */
  const dA = distance(comA, sitz.com)
  const dB = distance(comB, sitz.com)
  const steiner = mA * dA * dA + mB * dB * dB
  const restQuer = Math.max(0, sitz.transverse - steiner)

  const teil = (m: number, label: string, com: Vec3): Inertia => ({
    label,
    mass: m,
    com,
    transverse: (restQuer * m) / sitz.mass,
    axial: (sitz.axial * m) / sitz.mass,
    axis: achse ?? sitz.axis,
  })

  return { a: teil(mA, sitz.label, comA), b: teil(mB, labelB, comB) }
}

const distance = (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z)

/**
 * Der Rumpf, geteilt am Kreuzgelenk.
 *
 * DAS HAT DER ECHTE COMPILER GEFUNDEN, keine statische Prüfung: „more than 6
 * dofs in body 'cross'". Der Ursprung des Punktsatzes ist das Kreuzgelenk, und
 * der erste Entwurf machte daraus den freien Körper — mit dem freien Gelenk
 * (sechs Freiheiten) UND den beiden Rumpfachsen darauf. MuJoCo lässt das nicht
 * zu, und zwar zu Recht: ein Gelenk dreht einen Körper gegen seinen Eltern, und
 * der freie Körper hat keinen.
 *
 * Also hängt der Rumpf jetzt so, wie er auch gebaut ist: das BECKEN ist die
 * Wurzel und trägt die Beine, die BRUST hängt über dem Kreuzgelenk darüber und
 * trägt Arme und Kopf. Die Rumpfmasse verteilt sich auf beide, nach den Längen
 * unter und über dem Kreuzgelenk.
 */
function splitTrunk(out: Map<string, Inertia>, rig: Rig): void {
  const rumpf = out.get('pelvis')
  if (!rumpf) return

  /* Der Rumpf reicht vom Becken zur Schultermitte; das Kreuzgelenk liegt
     dazwischen. Beide Längen im Rahmen des Beckens. */
  const becken = absolute(rig, 'pelvis')
  const kreuz = absolute(rig, rig.order[0]!)
  const schulter = absolute(rig, 'shoulder')
  const unten = Math.abs(kreuz.z - becken.z)
  const oben = Math.abs(schulter.z - kreuz.z)
  if (unten <= 0 || oben <= 0) return

  /* Die Brust ist der abgetrennte Teil; ihr Schwerpunkt liegt auf halber Höhe
     zwischen Kreuzgelenk und Schultermitte. */
  const anteil = oben / (unten + oben)
  const comBrust: Vec3 = { x: 0, y: 0, z: M((kreuz.z + schulter.z) / 2 - becken.z) }

  const geteilt = splitSeat(rumpf, anteil, comBrust, `${rumpf.label} · Brust`, { x: 0, y: 0, z: 1 })
  if (!geteilt) return

  out.set('pelvis', { ...geteilt.a, label: `${rumpf.label} · Becken` })
  /* Die Brust bekommt ihren Schwerpunkt im EIGENEN Rahmen. */
  out.set('chest', {
    ...geteilt.b,
    com: { x: 0, y: 0, z: M((kreuz.z + schulter.z) / 2 - absolute(rig, 'chest').z) },
  })
}

/**
 * Der Zeh bekommt seinen Anteil an der Fussmasse.
 *
 * WARUM ÜBERHAUPT. Das Zehengrundgelenk trägt die Feder — das einzige passive
 * Gelenk des Modells und einer der Gründe für dieses MJCF. Ein Gelenk an einem
 * Körper ohne Masse rechnet MuJoCo nicht. Ohne diesen Schritt fiele die Feder
 * aus der Datei, und mit ihr die Frage, wegen der sie gebaut wurde.
 *
 * DER UNTERSCHIED ZUR HAND: dort gibt es keine Masse zu verteilen. Siehe
 * `gaps()`.
 */
function splitToe(
  out: Map<string, Inertia>,
  model: HumanoidModel,
  skeleton: Skeleton,
  rig: Rig,
): void {
  const fuss = out.get('ankle')
  if (!fuss) return
  const lZeh = Math.hypot(skeleton.toe.x, skeleton.toe.y)
  /* Die LÄNGE DES FUSSES ist Ferse bis Ballen und nicht Sprunggelenk bis
     Ballen: das Sprunggelenk sitzt mitten darüber, und wer von dort misst,
     lässt die halbe Ferse weg und gibt dem Zeh zu viel. */
  const lFuss = skeleton.ball.x - skeleton.heel.x
  if (lZeh <= 0 || lFuss <= 0) return

  /* Die Zehe liegt im Rahmen des Zehengrundgelenks: x nach vorn, z nach oben.
     Das Fusssystem des Skeletts führt y nach oben — daher der Tausch. */
  const mtpAt = towardVector(rig, 'ankle', 'mtp')
  const zehCom: Vec3 = { x: skeleton.toe.x / 2, y: 0, z: skeleton.toe.y / 2 }
  const zehImFuss: Vec3 = {
    x: mtpAt.x + zehCom.x,
    y: mtpAt.y + zehCom.y,
    z: mtpAt.z + zehCom.z,
  }

  const geteilt = splitSeat(
    fuss,
    lZeh / (lFuss + lZeh),
    zehImFuss,
    `${model.masses.foot.label} · Zehe`,
    unit(zehImFuss),
  )
  if (!geteilt) return

  out.set('ankle', geteilt.a)
  /* Die Zehe bekommt ihren Schwerpunkt im Rahmen des Zehengrundgelenks. */
  out.set('mtp', { ...geteilt.b, com: zehCom })
}

/**
 * Die Rumpfmitte im Rahmen des BECKENS.
 *
 * Der Kern setzt sie auf die Mitte zwischen Schultermitte und Becken. Hier wird
 * dieselbe Rechnung im Achsbaum gemacht; die Querlage fällt weg, weil die
 * Schultermitte zwischen zwei gespiegelten Punkten liegt.
 *
 * IM RAHMEN DES BECKENS UND NICHT DES PUNKTSATZES. Der Rumpf sitzt am Becken,
 * und das liegt 199 mm unter dem Ursprung der Bemassung. Wer die Mitte im
 * Ursprungsrahmen lässt, setzt eine Masse von 27 kg einen Vierteldezimeter zu
 * tief — mit einer Trägheitsbilanz, die MuJoCo daraufhin zurückweist.
 */
function trunkCentreOf(rig: Rig): Vec3 {
  const schulter = absolute(rig, 'shoulder')
  const becken = absolute(rig, 'pelvis')
  return {
    x: M((schulter.x + becken.x) / 2 - becken.x),
    y: 0,
    z: M((schulter.z + becken.z) / 2 - becken.z),
  }
}

/**
 * Wie hoch die Wurzel steht, damit der Roboter gerade eben über dem Boden hängt.
 *
 * NICHT DIE SOHLE, SONDERN DER TIEFSTE KONTAKTPUNKT. Der Sohlenpunkt des
 * Punktsatzes liegt unter dem Sprunggelenk; die Zehenspitze hängt in der
 * Grundstellung ein paar Millimeter tiefer. Wer die Sohle nimmt, lässt den
 * Roboter mit den Zehen im Boden starten — und MuJoCo drückt ihn im ersten
 * Schritt heraus, mit einer Bodenkraft, die nichts mit dem Gang zu tun hat.
 *
 * Zwei Millimeter Luft darüber: genug, damit der erste Schritt nicht im Boden
 * beginnt, zu wenig, um als Fallhöhe zu wirken.
 */
function startHeight(rig: Rig, skeleton: Skeleton): number {
  const ankle = M(absolute(rig, 'ankle').z)
  const mtp = M(absolute(rig, 'mtp').z)
  const tiefster = Math.min(
    ankle + skeleton.heel.y,
    ankle + skeleton.ball.y,
    mtp + skeleton.toe.y,
  )
  return -tiefster + 0.002
}

/** Lage eines Knotens gegenüber der Wurzel, mm. */
function absolute(rig: Rig, id: string): Vec3 {
  let at: string | null = id
  const out = { x: 0, y: 0, z: 0 }
  while (at) {
    const node: RigNode | undefined = rig.byId[at]
    if (!node) break
    out.x += node.at.x
    out.y += node.at.y
    out.z += node.at.z
    at = node.parent
  }
  return out
}

/** Der Weg von einem Knoten zu einem tieferen, in METERN und im Rahmen des ersten. */
function towardVector(rig: Rig, from: string, to: string): Vec3 {
  const a = absolute(rig, from)
  const b = absolute(rig, to)
  return { x: M(b.x - a.x), y: M(b.y - a.y), z: M(b.z - a.z) }
}

function unit(v: Vec3): Vec3 | null {
  const l = Math.hypot(v.x, v.y, v.z)
  if (l <= 0) return null
  return { x: v.x / l, y: v.y / l, z: v.z / l }
}

/* -------------------------------------------------------------------------
   Der Erzeuger
   ------------------------------------------------------------------------- */

/** Nur die Datei. Für alles Weitere `mjcfModel()`. */
export function toMjcf(
  model: HumanoidModel,
  skeleton: Skeleton,
  rig: Rig,
  options: MjcfOptions = {},
): string {
  return mjcfModel(model, skeleton, rig, options).xml
}

export function mjcfModel(
  model: HumanoidModel,
  skeleton: Skeleton,
  rig: Rig,
  options: MjcfOptions = {},
): MjcfResult {
  const name = options.name ?? 'gen5'
  const off = options.off ?? new Set<string>()
  const timestep = options.timestep ?? 0.001
  const mechs = new Map(mechanisms(rig).map((m) => [m.node, m]))
  const sitze = seats(model, skeleton, rig)
  const luecken = gaps(rig, sitze, off)
  const gesperrt = new Set(luecken.flatMap((g) => g.axes.map((a) => `${g.node}.${a}`)))

  const ctx: Ctx = {
    rig,
    model,
    skeleton,
    sitze,
    mechs,
    off,
    gesperrt,
    joints: [],
    bodies: [],
    kinder: childIndex(rig),
  }

  const wurzel = rig.byId[MJCF_ROOT] ?? rig.byId[rig.order[0]!]!
  /* Die Starthöhe misst von der MJCF-Wurzel, nicht vom Ursprung des
     Punktsatzes: das Becken liegt 199 mm unter dem Kreuzgelenk. */
  const hoehe = startHeight(rig, skeleton) + M(absolute(rig, wurzel.id).z)

  const out: string[] = []
  out.push('<?xml version="1.0" encoding="utf-8"?>')
  out.push('<!--')
  out.push('  Erzeugt aus dem Achsbaum des NEURA-Performance-Simulators.')
  out.push('  NICHT VON HAND AENDERN - die Quelle ist der Punktesatz, nicht diese Datei.')
  out.push('')
  if (options.modelId) out.push(`  Modellstand: ${escape(options.modelId)}`)
  out.push(`  Eigenmasse des Modells: ${fixed(ownMass(model))} kg`)
  out.push('  Die Massen stehen so, wie der Rechenkern sie fuehrt - sieben Segmente.')
  out.push('')
  out.push('  Kontaktgroessen (Reibung, Nachgiebigkeit, Daempfung) sind ANGENOMMEN')
  out.push('  und nicht gemessen. Bodenkraftspitzen duerfen deshalb gegen die')
  out.push('  inverse Dynamik abweichen; die Impulsbilanz darf es nicht.')
  for (const g of luecken) {
    out.push('')
    out.push(`  FEHLT: ${escape(g.node)} (${g.axes.join(', ')}) - ${escape(g.reason)}`)
  }
  out.push('-->')
  out.push(`<mujoco model="${escape(name)}">`)

  /* `angle="degree"` gilt fuer Gelenkgrenzen — aber NICHT fuer `keyframe qpos`.
     Siehe Kopfkommentar, Falle 2. */
  out.push('  <compiler angle="degree" coordinate="local" autolimits="true"/>')
  out.push(
    `  <option timestep="${fixed(timestep)}" gravity="0 0 -9.81" integrator="implicitfast"/>`,
  )

  out.push('  <default>')
  out.push(`    <joint damping="${fixed(JOINT_DAMPING)}" armature="0.01"/>`)
  out.push('    <geom rgba="0.55 0.6 0.65 1" condim="3"/>')
  out.push('  </default>')

  out.push('  <worldbody>')
  out.push(
    `    <geom name="boden" type="plane" size="20 20 0.1" rgba="0.3 0.32 0.34 1" ` +
      `friction="${fixed(GROUND_FRICTION)} 0.005 0.0001"/>`,
  )
  out.push('    <light pos="0 0 4" dir="0 0 -1" directional="true"/>')
  out.push(...body(ctx, wurzel.id, '', { x: 0, y: 0, z: hoehe * 1000 }, [], 4, true))
  out.push('  </worldbody>')

  out.push(...actuators(ctx))
  out.push(...keyframe(ctx, hoehe, options.pose ?? {}))

  out.push('</mujoco>')

  let masse = 0
  for (const b of ctx.bodies) {
    const sitz = sitze.get(baseName(b))
    if (sitz) masse += sitz.mass
  }

  return {
    xml: out.join('\n') + '\n',
    joints: ctx.joints,
    bodies: ctx.bodies,
    mass: masse,
    gaps: luecken,
  }
}

/* -------------------------------------------------------------------------
   Die Lücken — Achsen, die das MJCF nicht tragen kann
   ------------------------------------------------------------------------- */

/**
 * Achsen ohne Masse unter sich.
 *
 * DIE HAND IST DER FALL, UM DEN ES GEHT. Der Achsbaum führt ein Handgelenk mit
 * zwei Freiheiten, die Bauteilliste eine Hand mit Länge und Radius — das
 * Massenmodell des Kerns führt sie nicht. Die Unterarmmasse von 1,21 kg
 * entspricht dem Literaturwert für Unterarm UND Hand, Schwerpunkt und
 * Trägheitsradius dagegen denen des blossen Unterarms. Beides zusammen kann
 * nicht stimmen.
 *
 * Solange das ungeklärt ist, kann dieses MJCF die Handgelenkachsen nicht
 * rechnen: MuJoCo integriert keinen Körper ohne Masse. Sie stehen als
 * Kommentar in der Datei und als Lücke in `MjcfResult.gaps` — sichtbar, statt
 * mit hundert Gramm überdeckt.
 */
function gaps(rig: Rig, sitze: Map<string, Inertia>, off: ReadonlySet<string>): MjcfGap[] {
  const kinder = childIndex(rig)
  const out: MjcfGap[] = []

  /* Trägt dieser Knoten oder irgendein Knoten unter ihm eine Masse? */
  const traegt = (id: string): boolean => {
    if (sitze.has(id)) return true
    return (kinder.get(id) ?? []).some(traegt)
  }

  for (const id of rig.order) {
    /* Die Achsen der umgehängten Knoten sitzen an ihrem neuen Körper und
       fallen nicht ins Leere — siehe `REHANG`. */
    if (Object.values(REHANG).some((r) => r.joints.includes(id))) continue
    const node = rig.byId[id]!
    if (traegt(id)) continue
    const achsen = AXIS_KEYS.filter(
      (a) => node.freedom[a] && !off.has(`${id}.${a}`) && !off.has(id),
    )
    if (achsen.length === 0) continue
    out.push({
      node: id,
      axes: achsen,
      reason:
        'kein Massensegment an diesem Knoten oder darunter - MuJoCo rechnet keinen Koerper ohne Masse',
    })
  }
  return out
}

/* -------------------------------------------------------------------------
   Der Körperbaum
   ------------------------------------------------------------------------- */

interface Ctx {
  rig: Rig
  model: HumanoidModel
  skeleton: Skeleton
  sitze: Map<string, Inertia>
  mechs: Map<string, ReturnType<typeof mechanisms>[number]>
  off: ReadonlySet<string>
  gesperrt: ReadonlySet<string>
  joints: MjcfJoint[]
  bodies: string[]
  kinder: Map<string, string[]>
}

/** Ein Gelenk auf dem Weg zum nächsten Körper. */
interface Carried {
  node: RigNode
  axis: AxisKey
  /** Lage gegenüber dem ELTERNKÖRPER, mm — wird beim Setzen umgerechnet. */
  at: Vec3
}

/**
 * Ein Körper und alles darunter.
 *
 * DIE DRITTE FALLE, und sie kostet den ganzen Aufbau: DER ACHSBAUM HAT MEHR
 * KNOTEN, ALS DAS MJCF KÖRPER HABEN DARF. Die Hüfte ist im Baum `hipG`, `hipR`,
 * `hipN` — drei Knoten mit je einer Achse, und die Masse des Beins sitzt am
 * letzten. Wer daraus drei Körper macht, bekommt zwei ohne Masse mit je einem
 * Gelenk, und MuJoCo rechnet das nicht.
 *
 * Deshalb entsteht ein Körper NUR dort, wo eine Masse sitzt. Alles dazwischen
 * wird eingeklappt: die Gelenke wandern in den Körper darunter und behalten
 * über `pos` ihren richtigen Ort. Das ist kein Kunstgriff, sondern das, wofür
 * MJCF gemacht ist — mehrere Gelenke an einem Körper, ohne die erfundenen
 * Zwischenglieder, die URDF erzwingt.
 *
 * `seite` wird nach unten durchgereicht. Ein gespiegelter Knoten unter einem
 * gespiegelten Knoten gehört zu EINER Seite; wer an jeder Stufe erneut
 * spiegelt, bekommt vier Beine, dann acht — beim ersten Anlauf waren es 189
 * Gelenke statt 31.
 */
function body(
  ctx: Ctx,
  id: string,
  seite: '' | 'R' | 'L',
  vor: Vec3,
  carried: Carried[],
  tiefe: number,
  istWurzel = false,
  eigenVor?: Vec3,
): string[] {
  const node = ctx.rig.byId[id]!

  /* Ein noch seitenloser Knoten, der gespiegelt ist, öffnet beide Seiten. */
  if (seite === '' && node.mirrored && !istWurzel) {
    return [
      ...body(ctx, id, 'R', vor, carried, tiefe, false, eigenVor),
      ...body(ctx, id, 'L', mirror(vor), carried.map(mirrorCarried), tiefe, false, eigenVor),
    ]
  }

  /* Die eigene Lage kommt vom Knoten — links aus `spiegel()`, damit eine
     gewollte Unsymmetrie sie schlägt. Wurzel und umgehängte Knoten bekommen
     sie vom Aufrufer. */
  const eigen =
    eigenVor ?? (istWurzel ? { x: 0, y: 0, z: 0 } : seite === 'L' ? spiegel(node) : node.at)
  const at: Vec3 = { x: vor.x + eigen.x, y: vor.y + eigen.y, z: vor.z + eigen.z }

  const sitz = ctx.sitze.get(id)
  const eigene: Carried[] = AXIS_KEYS.filter((a) => node.freedom[a]).map((axis) => ({
    node,
    axis,
    at,
  }))
  const alle = [...carried, ...eigene]

  if (!sitz) {
    /* Kein Körper: Lage und Gelenke gehen an die Kinder weiter. */
    const out: string[] = []
    for (const kind of childrenOf(ctx, id)) {
      out.push(...body(ctx, kind.id, seite, at, [...alle, ...kind.carried(ctx, at)], tiefe, false, kind.eigen))
    }
    return out
  }

  const pad = ' '.repeat(tiefe)
  const bodyName = `${id}${seite ? `_${seite}` : ''}`
  ctx.bodies.push(bodyName)

  const out: string[] = []
  out.push(
    `${pad}<body name="${escape(bodyName)}" pos="${fixed(M(at.x))} ${fixed(M(at.y))} ${fixed(M(at.z))}">`,
  )
  if (istWurzel) {
    out.push(`${pad}  <freejoint name="wurzel"/>`)
  }
  out.push(...inertial(sitz, seite, tiefe + 2))
  for (const c of alle) {
    out.push(...joint(ctx, c, at, seite, bodyName, tiefe + 2))
  }
  out.push(...visual(ctx, id, seite, tiefe + 2))
  out.push(...contact(ctx, id, seite, tiefe + 2))

  const null3 = { x: 0, y: 0, z: 0 }
  for (const kind of childrenOf(ctx, id)) {
    out.push(
      ...body(ctx, kind.id, seite, null3, kind.carried(ctx, null3), tiefe + 2, false, kind.eigen),
    )
  }
  out.push(`${pad}</body>`)
  return out
}

/* -------------------------------------------------------------------------
   Die Wurzel des MJCF ist nicht die des Punktsatzes
   ------------------------------------------------------------------------- */

/**
 * WO DER MJCF-BAUM VOM ACHSBAUM ABWEICHT — und warum er es muss.
 *
 * Der Punktsatz hat seinen Ursprung im KREUZGELENK: alle sechzehn Punkte sind
 * von dort aus bemasst, und der Achsbaum erbt das. Für eine Bemassung ist das
 * eine gute Wahl; für die Physik ist es keine. Der echte MuJoCo-Compiler sagt
 * es in einem Satz:
 *
 *     Loading error: more than 6 dofs in body 'cross'
 *
 * Der freie Körper trägt sechs Freiheiten, und das Kreuzgelenk wollte zwei
 * weitere daraufsetzen. Ein Gelenk dreht einen Körper gegen SEINEN ELTERN, und
 * der freie Körper hat keinen. Der Ursprung des Punktsatzes ist ein
 * GELENKPUNKT und kein Glied.
 *
 * Deshalb hängt der Rumpf hier so, wie er auch gebaut ist: das BECKEN ist die
 * Wurzel und trägt die Beine, die BRUST hängt über dem Kreuzgelenk darüber und
 * trägt Arme und Kopf. Die beiden Rumpfachsen sitzen zwischen ihnen — an ihrem
 * eigenen Ort, nicht am Ursprung eines der beiden Körper.
 *
 * DAS IST KEINE ZWEITE GEOMETRIE. Jede Zahl kommt weiter aus dem Punktsatz;
 * verschoben ist nur, WELCHER Punkt Wurzel heisst.
 */
const MJCF_ROOT = 'pelvis'

/**
 * Knoten, die im MJCF woanders hängen als im Achsbaum.
 *
 * `joints` sind die Knoten des Achsbaums, deren Achsen dabei mitwandern — beim
 * Rumpf ist das das Kreuzgelenk selbst.
 */
const REHANG: Readonly<Record<string, { under: string; joints: readonly string[] }>> = {
  chest: { under: MJCF_ROOT, joints: ['cross'] },
}

interface Child {
  id: string
  /** Lage gegenüber dem Elternknoten, mm — `undefined` heisst: die des Achsbaums. */
  eigen?: Vec3
  /** Achsen, die beim Umhängen mitkommen. */
  carried: (ctx: Ctx, vor: Vec3) => Carried[]
}

const KEINE = (): Carried[] => []

/** Die Kinder eines Körpers — die des Achsbaums plus die umgehängten. */
function childrenOf(ctx: Ctx, id: string): Child[] {
  const out: Child[] = (ctx.kinder.get(id) ?? [])
    .filter((kind) => !(kind in REHANG))
    .map((kind) => ({ id: kind, carried: KEINE }))

  for (const [kind, wohin] of Object.entries(REHANG)) {
    if (wohin.under !== id) continue
    if (!ctx.rig.byId[kind]) continue
    const hier = absolute(ctx.rig, id)
    const dort = absolute(ctx.rig, kind)
    out.push({
      id: kind,
      eigen: { x: dort.x - hier.x, y: dort.y - hier.y, z: dort.z - hier.z },
      carried: (c, vor) =>
        wohin.joints.flatMap((gelenk) => {
          const node = c.rig.byId[gelenk]
          if (!node) return []
          const ort = absolute(c.rig, gelenk)
          const at: Vec3 = {
            x: vor.x + ort.x - hier.x,
            y: vor.y + ort.y - hier.y,
            z: vor.z + ort.z - hier.z,
          }
          return AXIS_KEYS.filter((a) => node.freedom[a]).map((axis) => ({ node, axis, at }))
        }),
    })
  }
  return out
}

/**
 * Der Achsbaum von der MJCF-Wurzel aus.
 *
 * Die Knoten ÜBER der Wurzel — beim Gen5 nur das Kreuzgelenk — sind keine
 * Körper mehr; ihre Achsen wandern über `REHANG` an den Körper, den sie
 * bewegen.
 */
function childIndex(rig: Rig): Map<string, string[]> {
  const out = new Map<string, string[]>()
  for (const id of rig.order) {
    const p = rig.byId[id]!.parent
    if (p === null) continue
    const liste = out.get(p)
    if (liste) liste.push(id)
    else out.set(p, [id])
  }
  return out
}

/**
 * Die linke Seite eines paarigen Knotens.
 *
 * `atLeft` gewinnt, wenn es gesetzt ist — eine gewollte Unsymmetrie ist eine
 * Konstruktionsentscheidung und darf nicht weggespiegelt werden. Sonst wird die
 * Querachse negiert.
 */
function spiegel(node: RigNode): Vec3 {
  if (node.atLeft) return node.atLeft
  return { x: node.at.x, y: -node.at.y, z: node.at.z }
}

const mirror = (v: Vec3): Vec3 => ({ x: v.x, y: -v.y, z: v.z })
const mirrorCarried = (c: Carried): Carried => ({ ...c, at: mirror(c.at) })

const baseName = (body: string): string => body.replace(/_[RL]$/, '')

/* -------------------------------------------------------------------------
   Trägheit
   ------------------------------------------------------------------------- */

/**
 * Masse und Trägheit eines Körpers.
 *
 * `fullinertia` UND NICHT `diaginertia`: die Hauptachsen eines Glieds liegen
 * entlang seiner Längsachse, und die zeigt nicht bei jedem Körper in die
 * Richtung einer Koordinatenachse — der Fuss zeigt nach vorn, die Beine nach
 * unten. Mit `diaginertia` müsste dazu eine Drehung des Trägheitsrahmens
 * angegeben werden; `fullinertia` sagt dasselbe in einer Zeile und ohne die
 * Gelegenheit, das Vorzeichen einer Drehung zu verwechseln.
 *
 * Der Tensor ist der eines Stabes: quer der Trägheitsradius des Kerns, längs
 * der Vollzylinder. Punktmassen bekommen beides gleich.
 */
function inertial(sitz: Inertia, seite: '' | 'R' | 'L', tiefe: number): string[] {
  const pad = ' '.repeat(tiefe)
  const com = seite === 'L' ? mirror(sitz.com) : sitz.com
  const u = sitz.axis ? (seite === 'L' ? mirror(sitz.axis) : sitz.axis) : null

  const It = sitz.transverse
  const d = sitz.axial - It
  const ux = u?.x ?? 0
  const uy = u?.y ?? 0
  const uz = u?.z ?? 0

  const ixx = It + d * ux * ux
  const iyy = It + d * uy * uy
  const izz = It + d * uz * uz
  const ixy = d * ux * uy
  const ixz = d * ux * uz
  const iyz = d * uy * uz

  return [
    `${pad}<!-- ${escape(sitz.label)} -->`,
    `${pad}<inertial pos="${fixed(com.x)} ${fixed(com.y)} ${fixed(com.z)}" ` +
      `mass="${fixed(sitz.mass)}" ` +
      `fullinertia="${fixed(ixx)} ${fixed(iyy)} ${fixed(izz)} ${fixed(ixy)} ${fixed(ixz)} ${fixed(iyz)}"/>`,
  ]
}

/* -------------------------------------------------------------------------
   Gelenke
   ------------------------------------------------------------------------- */

const AXIS_VECTOR: Record<AxisKey, string> = { x: '1 0 0', y: '0 1 0', z: '0 0 1' }

/**
 * Eine Achse als MuJoCo-Drehgelenk.
 *
 * `pos` ist der Ort der Achse IM KÖRPERRAHMEN. Bei einem eingeklappten Gelenk
 * ist das ein Punkt oberhalb des Körperursprungs — die Hüftgierachse sitzt
 * 26 cm über der Nickachse, an der das Bein hängt. Ohne diesen Versatz drehte
 * sie im falschen Punkt, und das Bein schwänge beim Abspreizen aus.
 */
function joint(
  ctx: Ctx,
  c: Carried,
  bodyAt: Vec3,
  seite: '' | 'R' | 'L',
  bodyName: string,
  tiefe: number,
): string[] {
  const pad = ' '.repeat(tiefe)
  const node = c.node
  const key = `${node.id}.${c.axis}`
  if (ctx.off.has(key) || ctx.off.has(node.id)) {
    return [`${pad}<!-- ${escape(key)}: stillgelegt, kein Gelenk -->`]
  }
  if (ctx.gesperrt.has(key)) {
    return [`${pad}<!-- ${escape(key)}: keine Masse darunter, siehe Kopf der Datei -->`]
  }

  const freedom = node.freedom[c.axis]!
  const name = `${node.id}_${c.axis}${seite ? `_${seite}` : ''}`
  const feder = node.id === 'mtp'
  const pos: Vec3 = {
    x: c.at.x - bodyAt.x,
    y: c.at.y - bodyAt.y,
    z: c.at.z - bodyAt.z,
  }

  const out: string[] = []
  const mech = ctx.mechs.get(node.id)
  if (mech && mech.ratio.kind !== 'zahl') {
    out.push(
      `${pad}<!-- ${escape(node.label)}: ${mech.ratio.kind === 'kurve' ? 'Kurbelschwinge' : 'Parallelkinematik'},`,
      `${pad}     geschlossene Kette. Steht als EIN Drehgelenk; die Uebersetzung`,
      `${pad}     ${fixed(mech.nominal)} : 1 sitzt am Antrieb. -->`,
    )
  }

  out.push(
    `${pad}<joint name="${escape(name)}" type="hinge" axis="${AXIS_VECTOR[c.axis]}" ` +
      `pos="${fixed(M(pos.x))} ${fixed(M(pos.y))} ${fixed(M(pos.z))}" ` +
      `range="${fixed(freedom.lo)} ${fixed(freedom.hi)}"` +
      (feder
        ? /*
            DIE ZEHENFEDER IST DAS EINZIGE PASSIVE GELENK.

            Sie bekommt keinen Antrieb, sondern Steifigkeit und Ruhelage.
            MuJoCo rechnet in rad; die Kennlinie des Modells steht in Nm je
            GRAD, daher der Faktor 180/pi. Aus 3,4 Nm/Grad werden 195 Nm/rad —
            die steifste Feder im Modell und der Grund, warum die Schrittweite
            eine Eingabe ist.
          */
          ` stiffness="${fixed(TOE_SPRING.rate / DEG)}" springref="0" damping="0.5"`
        : '') +
      '/>',
  )

  ctx.joints.push({
    name,
    node: node.id,
    axis: c.axis,
    side: seite,
    body: bodyName,
    lo: freedom.lo,
    hi: freedom.hi,
    moment: freedom.moment,
    spring: feder,
  })
  return out
}

/* -------------------------------------------------------------------------
   Sichtform und Kontakt
   ------------------------------------------------------------------------- */

/**
 * Das Glied als Kapsel — NUR ZUM ANSEHEN.
 *
 * `contype="0" conaffinity="0"`: sie stösst mit nichts zusammen. Ein Modell,
 * dessen Glieder alle kollidieren, bleibt in der eigenen Hüfte hängen; die
 * Freigangsprüfung dieses Projekts ist eine eigene Rechnung und gehört nicht in
 * die Physik. Sie trägt auch keine Masse — die steht im `inertial` darüber und
 * würde sonst doppelt zählen.
 */
function visual(ctx: Ctx, id: string, seite: '' | 'R' | 'L', tiefe: number): string[] {
  const seat = SEATS.find((s) => s.node === id)
  if (!seat?.toward) return []
  const roh = towardVector(ctx.rig, id, seat.toward)
  const bis = seite === 'L' ? mirror(roh) : roh
  const r = radiusOf(ctx, id)
  if (r === null) return []

  return [
    `${tab(tiefe)}<geom name="g_${escape(id)}${seite ? `_${seite}` : ''}" type="capsule" ` +
      `fromto="0 0 0 ${fixed(bis.x)} ${fixed(bis.y)} ${fixed(bis.z)}" size="${fixed(r)}" ` +
      `contype="0" conaffinity="0" mass="0" group="2"/>`,
  ]
}

function radiusOf(ctx: Ctx, id: string): number | null {
  for (const p of parts(ctx.model, ctx.skeleton)) {
    if (p.carrier === id) return p.radius
  }
  return null
}

/**
 * Die Sohle als Kontaktgeometrie.
 *
 * NUR AM FUSS UND NUR DORT. Ferse und Ballen sitzen am Sprunggelenk, die Zehe
 * am Zehengrundgelenk — DORT UND NICHT AM FUSS, weil sie sonst nicht mitdrehte
 * und die Zehenfeder nichts zu tun hätte.
 *
 * Drei Punkte je Fuss, weil zwei kippeln und vier eine Ebene überbestimmen.
 */
function contact(ctx: Ctx, id: string, seite: '' | 'R' | 'L', tiefe: number): string[] {
  const pad = tab(tiefe)
  const s = seite ? `_${seite}` : ''
  const sk = ctx.skeleton
  /* Das Fusssystem des Skeletts führt y nach oben, der Achsbaum z. */
  const punkt = (v: Vec3): string =>
    `${fixed(v.x)} 0 ${fixed(v.y + CONTACT_RADIUS)}`

  if (id === 'ankle') {
    return [
      `${pad}<!-- Sohle: Ferse und Ballen. Die Zehe sitzt am Zehengrundgelenk. -->`,
      `${pad}<geom name="ferse${s}" type="sphere" size="${fixed(CONTACT_RADIUS)}" pos="${punkt(sk.heel)}"/>`,
      `${pad}<geom name="ballen${s}" type="sphere" size="${fixed(CONTACT_RADIUS)}" pos="${punkt(sk.ball)}"/>`,
    ]
  }
  if (id === 'mtp') {
    return [
      `${pad}<geom name="zehe${s}" type="sphere" size="${fixed(CONTACT_RADIUS)}" pos="${punkt(sk.toe)}"/>`,
    ]
  }
  return []
}

/* -------------------------------------------------------------------------
   Antriebe
   ------------------------------------------------------------------------- */

/**
 * Ein Antrieb je angetriebener Achse, mit der Momentgrenze aus der Auslegung.
 *
 * DAS IST DER PUNKT, AN DEM DIE GEGENPROBE ENTSTEHT. `ctrlrange` ist genau das
 * Moment, das die Achse laut Modell hat — 212 Nm an der Hüftnickachse, 300 am
 * Knie. Solange es reicht, folgt die Achse ihrer Kurve. Sobald es nicht reicht,
 * läuft der Regler in die Begrenzung, und der Folgefehler wächst GENAU an der
 * Stelle im Zyklus, an der die Auslegung zu schwach ist.
 *
 * Das Zehengelenk bekommt keinen: es ist eine Feder.
 */
function actuators(ctx: Ctx): string[] {
  const out: string[] = ['  <actuator>']
  for (const j of ctx.joints) {
    if (j.spring) {
      out.push(`    <!-- ${escape(j.name)}: passive Feder, kein Antrieb -->`)
      continue
    }
    const mech = ctx.mechs.get(j.node)
    const uebersetzung =
      mech && mech.ratio.kind !== 'zahl'
        ? ` <!-- Uebersetzung ${fixed(mech.nominal)}:1 aus der Koppelkinematik -->`
        : ''
    out.push(
      `    <motor name="m_${escape(j.name)}" joint="${escape(j.name)}" ` +
        `ctrlrange="${fixed(-j.moment)} ${fixed(j.moment)}" gear="1"/>${uebersetzung}`,
    )
  }
  out.push('  </actuator>')
  return out
}

/* -------------------------------------------------------------------------
   Startlage — Falle 2
   ------------------------------------------------------------------------- */

/**
 * Die Startlage als Keyframe.
 *
 * HIER LIEGT DIE FALLE, an der die erste Fassung des Python-Erzeugers
 * gescheitert ist: `compiler angle="degree"` gilt für Gelenkgrenzen und für
 * Winkel IM BODY — aber NICHT für `keyframe qpos`. Dort erwartet MuJoCo immer
 * Bogenmass. Wer Grad hineinschreibt, bekommt einen Roboter in einer Haltung,
 * die niemand gemeint hat; gemessen wurde damals ein Schwerpunkt 17 cm zu hoch.
 *
 * Die Reihenfolge ist die der DATEI und wird deshalb nicht zweitens abgeleitet,
 * sondern aus der Liste genommen, die beim Schreiben entstanden ist. Eine
 * zweite Ableitung wäre nicht falsch sichtbar — sie drehte nur die falschen
 * Gelenke.
 */
function keyframe(ctx: Ctx, hoehe: number, pose: Readonly<Record<string, number>>): string[] {
  const werte: string[] = ['0', '0', fixed(hoehe), '1', '0', '0', '0']
  for (const j of ctx.joints) {
    const grad = pose[`${j.node}.${j.axis}`] ?? 0
    /* Grad in Bogenmass — siehe Kopfkommentar. */
    werte.push(fixed(grad * DEG))
  }
  return [
    '  <keyframe>',
    '    <!-- qpos steht im BOGENMASS, auch bei compiler angle="degree". -->',
    `    <key name="start" qpos="${werte.join(' ')}"/>`,
    '  </keyframe>',
  ]
}

/* -------------------------------------------------------------------------
   Handwerk
   ------------------------------------------------------------------------- */

/** Sechs Stellen: darunter wandert ein Gelenkpunkt beim Hin- und Herrechnen. */
const fixed = (v: number): string => (Number.isFinite(v) ? v.toFixed(6) : '0.000000')

const tab = (n: number): string => ' '.repeat(n)

const escape = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
